import { useEffect, useRef, useState } from "react";

/**
 * useResponsiveLayout (step 7.1) — owns the responsive layout chrome that was
 * scattered through ChatLayout: the mobile drawers/sheets, the swipe-to-open
 * gesture, the desktop column resizers, the md breakpoint, and the
 * Composer-measured height (whose grow-effect nudges the message list).
 *
 * Inputs are minimal:
 *  - currentVoidId: gates the swipe gesture (no drawers when no space selected)
 *    and derives `showPeople` for the right-edge (users) drawer.
 *  - listRef: the message-list scroll container — the composerHeight grow-effect
 *    keeps the newest message visible when the composer grows.
 *
 * Deliberately NOT here: `channelPaneView` (navigation/view-mode state, tangled
 * with switchVoid/useDms) and the two chanSheetOpen↔channelPaneView reconciliation
 * effects, which stay in the composition layer and consume the returned
 * chanSheetOpen/setChanSheetOpen/setChannelPaneView.
 */
/** Tracks a CSS media query; reads it synchronously so the first render already has the right layout. */
function useMediaQuery(query: string): boolean {
  const supported = typeof window !== "undefined" && typeof window.matchMedia === "function";
  const [matches, setMatches] = useState<boolean>(() => (supported ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (!supported) return;
    const mq = window.matchMedia(query);
    const apply = () => setMatches(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [query, supported]);
  return matches;
}

export function useResponsiveLayout({
  currentVoidId,
  listRef,
}: {
  currentVoidId: string;
  listRef: { current: HTMLDivElement | null };
}) {
  // sheets (mobile)
  const [chanSheetOpen, setChanSheetOpen] = useState(false);
  const [usersSheetOpen, setUsersSheetOpen] = useState(false);

  // Mobile swipe gesture state (interactive drawers)
  const [swipePanel, setSwipePanel] = useState<null | 'chan' | 'users'>(null);
  const [swipeMode, setSwipeMode] = useState<null | 'open' | 'close'>(null);
  const [swipeProgress, setSwipeProgress] = useState(0); // 0..1
  const swipeProgRef = useRef(0);
  const swipePanelRef = useRef<null | 'chan' | 'users'>(null);
  const swipeModeRef = useRef<null | 'open' | 'close'>(null);
  const chanOpenRef = useRef(false);
  const usersOpenRef = useRef(false);
  useEffect(() => { chanOpenRef.current = chanSheetOpen; }, [chanSheetOpen]);
  useEffect(() => { usersOpenRef.current = usersSheetOpen; }, [usersSheetOpen]);

  const showPeople = !!currentVoidId;

  // --- Mobile gestures: swipe anywhere to open drawers ---
  useEffect(() => {
    const el = document;
    // Detect browser support for passive event listeners and prepare options objects safely
    let supportsPassive = false;
    try {
      const opts = Object.defineProperty({}, 'passive', {
        get() {
          supportsPassive = true;
          return false;
        },
      }) as AddEventListenerOptions;
      const noop = () => undefined;
      window.addEventListener('test-passive', noop, opts);
      window.removeEventListener('test-passive', noop, opts);
    } catch {
      /* noop */
    }
    const optPassiveTrue: AddEventListenerOptions | boolean = supportsPassive ? { passive: true } : false;
    const optPassiveFalse: AddEventListenerOptions | boolean = supportsPassive ? { passive: false } : false;
    let startX = 0, startY = 0, active = false, decided = false;
    const drawerW = Math.min(window.innerWidth * 0.8, 360); // px
    function onStart(e: TouchEvent) {
      if (!currentVoidId) return; // nothing when no space selected
      const t = e.touches[0]; if (!t) return;
      startX = t.clientX; startY = t.clientY; active = true; decided = false;
      const chanOpen = chanOpenRef.current;
      const usersOpen = usersOpenRef.current;
      const edgeZone = 56;
      const width = window.innerWidth || 0;
      const nearLeft = startX <= edgeZone;
      const nearRight = startX >= (width - edgeZone);
      if (!chanOpen && !usersOpen) {
        if (!nearLeft && !(showPeople && nearRight)) { active = false; return; }
      }
      setSwipePanel(null); setSwipeMode(null); setSwipeProgress(0);
      swipePanelRef.current = null; swipeModeRef.current = null; swipeProgRef.current = 0;
    }
    function onMove(e: TouchEvent) {
      if (!active) return;
      // An icon or row is being dragged to a new place (useSlotReorder): that's not a drawer swipe.
      if (document.body.dataset.reordering) { active = false; return; }
      const t = e.touches[0]; if (!t) return;
      const dx = t.clientX - startX; const dy = t.clientY - startY;
      if (!decided) {
        if (Math.abs(dx) > 16 && Math.abs(dx) > Math.abs(dy)) {
          decided = true;
          const chanOpen = chanOpenRef.current;
          const usersOpen = usersOpenRef.current;
          // If a drawer is open, prefer closing gesture (standard: left closes channels, right closes users)
          if (chanOpen && dx < 0) {
            setSwipePanel('chan'); swipePanelRef.current = 'chan';
            setSwipeMode('close'); swipeModeRef.current = 'close';
          } else if (usersOpen && dx > 0) {
            setSwipePanel('users'); swipePanelRef.current = 'users';
            setSwipeMode('close'); swipeModeRef.current = 'close';
          } else if (!chanOpen && !usersOpen) {
            // Decide panel based on initial direction (opening)
            if (dx > 0) {
              setSwipePanel('chan'); swipePanelRef.current = 'chan';
              setSwipeMode('open'); swipeModeRef.current = 'open';
              setUsersSheetOpen(false);
            } else if (dx < 0 && showPeople) {
              setSwipePanel('users'); swipePanelRef.current = 'users';
              setSwipeMode('open'); swipeModeRef.current = 'open';
              setChanSheetOpen(false);
            } else {
              decided = false; active = false; return;
            }
          } else {
            // Wrong direction for closing, ignore
            decided = false; active = false; return;
          }
        } else {
          return; // wait for clear horizontal intent
        }
      }
      // When actively swiping a panel, prevent vertical scroll jitter
      if (decided && e.cancelable) e.preventDefault();
      let dist = 0;
      if (swipeModeRef.current === 'open') {
        dist = Math.min(drawerW, Math.max(0, Math.abs(dx)));
      } else if (swipeModeRef.current === 'close') {
        if (swipePanelRef.current === 'chan') {
          dist = Math.min(drawerW, Math.max(0, -dx)); // closing chan with left swipe (dx negative)
        } else if (swipePanelRef.current === 'users') {
          dist = Math.min(drawerW, Math.max(0, dx)); // closing users with right swipe (dx positive)
        }
      }
      const p = Math.min(1, dist / drawerW);
      swipeProgRef.current = p;
      setSwipeProgress(p);
    }
    function onEnd() {
      if (!active) return; active = false;
      const prog = swipeProgRef.current;
      const panel = swipePanelRef.current;
      const mode = swipeModeRef.current;
      swipeProgRef.current = 0; swipePanelRef.current = null;
      swipeModeRef.current = null;
      setSwipeProgress(0); setSwipePanel(null); setSwipeMode(null);
      if (!panel) return;
      if (prog > 0.35) {
        if (mode === 'open') {
          if (panel === 'chan') { setChanSheetOpen(true); setUsersSheetOpen(false); }
          else if (panel === 'users') { setUsersSheetOpen(true); setChanSheetOpen(false); }
        } else if (mode === 'close') {
          if (panel === 'chan') { setChanSheetOpen(false); }
          else if (panel === 'users') { setUsersSheetOpen(false); }
        }
      }
    }
    el.addEventListener('touchstart', onStart, optPassiveTrue);
    el.addEventListener('touchmove', onMove, optPassiveFalse);
    el.addEventListener('touchend', onEnd, optPassiveTrue);
    return () => {
      el.removeEventListener('touchstart', onStart, optPassiveTrue);
      el.removeEventListener('touchmove', onMove, optPassiveFalse);
      el.removeEventListener('touchend', onEnd, optPassiveTrue);
    };
  }, [currentVoidId, showPeople]);

  // Resizable columns (desktop)
  const [chanW, setChanW] = useState<number>(() => {
    const v = Number(localStorage.getItem('chanW') || '208');
    return isFinite(v) && v >= 160 && v <= 420 ? v : 208;
  });
  const [peopleW, setPeopleW] = useState<number>(() => {
    const v = Number(localStorage.getItem('peopleW') || '224');
    return isFinite(v) && v >= 160 && v <= 420 ? v : 224;
  });
  useEffect(() => { try { localStorage.setItem('chanW', String(chanW)); } catch { /* noop */ } }, [chanW]);
  useEffect(() => { try { localStorage.setItem('peopleW', String(peopleW)); } catch { /* noop */ } }, [peopleW]);

  function startDrag(which: 'chan' | 'people', clientX: number) {
    const startX = clientX;
    const start = which === 'chan' ? chanW : peopleW;
    function onMove(ev: MouseEvent) {
      const dx = ev.clientX - startX;
      if (which === 'chan') setChanW(Math.max(160, Math.min(420, start + dx)));
      else setPeopleW(Math.max(160, Math.min(420, start - dx)));
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  // Composer height (measured by the Composer child) + md breakpoint
  const [composerHeight, setComposerHeight] = useState(0);
  // rem so the switch follows the user's text size. Keep in sync with `md` in tailwind.config.js.
  const isMdUp = useMediaQuery("(min-width: 48rem)");
  // The People column only stays open by default when there is room for it (see DesktopSidebars).
  const isWideUp = useMediaQuery("(min-width: 68.75rem)");
  const lastComposerHeightRef = useRef(0);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const prev = lastComposerHeightRef.current;
    lastComposerHeightRef.current = composerHeight;
    if (composerHeight <= prev) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < 120) {
      requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      });
    }
  }, [composerHeight, listRef]);

  return {
    // sheets
    chanSheetOpen, setChanSheetOpen,
    usersSheetOpen, setUsersSheetOpen,
    // swipe
    swipePanel, swipeMode, swipeProgress,
    // resizers
    chanW, peopleW, startDrag,
    // breakpoints + composer height
    isMdUp, isWideUp,
    composerHeight, setComposerHeight,
  };
}
